import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { http } from "../../lib/http";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";

const STEPS = ["family", "student", "medical", "gdpr", "payment", "review"];
const CEFR = ["pre_a1", "a1", "a1_plus", "a2", "a2_plus", "b1", "b1_plus", "b2", "b2_plus", "c1", "c1_plus", "c2"];

function emptyState() {
  return {
    family_mode: "new",            // "new" | "existing"
    family_id: "",
    family: {
      family_name: "",
      primary_parent: { first_name: "", last_name: "", nif_nie: "", phone: "", email: "", relationship: "mother" },
      address: { line1: "", city: "", postcode: "", province: "Madrid", country: "ES" },
      billing: { billing_name: "", billing_nif: "", billing_email: "", payment_method: "transfer", iban: "", bic: "" },
      gdpr: { data_processing_consent: false, marketing_consent: false, image_rights_consent: false, consent_date: new Date().toISOString().slice(0, 10) },
      notes: "",
      active: true,
    },
    student: {
      first_name: "", last_name: "", date_of_birth: "2015-01-01", level: "a1",
      dni_nie: "", phone: "", email: "", preferred_name: "",
      emergency_contact: { name: "", relationship: "", phone: "" },
      allergies: [],
    },
  };
}

export default function EnrollmentWizard({ open, onClose, onCommitted, leadPrefill }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [state, setState] = useState(emptyState());
  const [families, setFamilies] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [ibanCheck, setIbanCheck] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    if (leadPrefill) {
      const parts = (leadPrefill.full_name || "").split(" ", 2);
      setState({
        ...emptyState(),
        family_mode: "new",
        family: {
          ...emptyState().family,
          family_name: parts[1] || parts[0] || "Family",
          primary_parent: {
            first_name: (leadPrefill.parent_name || parts[0] || "").split(" ")[0],
            last_name: parts[1] || leadPrefill.parent_name || "",
            phone: leadPrefill.parent_phone || leadPrefill.phone || "",
            email: leadPrefill.email || "",
            relationship: leadPrefill.age_group === "adult" ? "self" : "guardian",
            nif_nie: "",
          },
        },
        student: {
          ...emptyState().student,
          first_name: parts[0] || "",
          last_name: parts[1] || "",
          level: leadPrefill.interested_level || "a1",
          phone: leadPrefill.phone || "",
          email: leadPrefill.email || "",
        },
        _from_lead_id: leadPrefill.id,
      });
    } else {
      // Check for an existing draft
      http.get("/enrollment/draft").then((r) => {
        if (r.data?.draft) {
          setResumePrompt(r.data.draft);
        } else {
          setState(emptyState());
        }
      }).catch(() => setState(emptyState()));
    }
    http.get("/families?limit=200&active=true").then((r) => setFamilies(r.data.items)).catch(() => {});
  }, [open, leadPrefill]);

  const validateIban = async (iban) => {
    setIbanCheck("checking");
    try {
      const r = await http.post("/poc/sepa/validate", {
        creditor: { name: state.family.billing.billing_name || "x", nif: "B12345678", iban,
                    bic: state.family.billing.bic || "x", creditor_id: "ES12ZZZ123456789" },
        mandates: [{
          mandate_id: "X", mandate_date: "2026-01-01", debtor_name: "x", debtor_nif: "12345678Z",
          debtor_iban: iban, debtor_bic: state.family.billing.bic || "x",
          amount_eur: 1.0, sequence_type: "FRST", invoice_ref: "x",
        }],
        collection_date: "2026-03-05",
      });
      const ibanError = (r.data.errors || []).find((e) => /iban/i.test(e.message || ""));
      setIbanCheck(ibanError ? { ok: false, msg: ibanError.message } : { ok: true });
    } catch { setIbanCheck({ ok: false, msg: "Check failed" }); }
  };

  const saveDraft = async () => {
    try {
      const r = await http.post("/enrollment/draft", { state, step });
      setDraftId(r.data.id);
      toast.success(t("wizard.draftSaved"));
    } catch { toast.error("Draft save failed"); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const payload = state.family_mode === "existing"
        ? { family_id: state.family_id, student: state.student, create_sepa_mandate: state.family.billing.payment_method === "sepa" }
        : { family: state.family, student: state.student, create_sepa_mandate: state.family.billing.payment_method === "sepa" };
      const r = await http.post("/enrollment/commit", payload);
      // Convert lead if any
      if (state._from_lead_id) {
        await http.post(`/leads/${state._from_lead_id}/convert`, {
          family_id: r.data.family.id,
          student_overrides: state.student,
        }).catch(() => {/* lead may already be linked */});
      }
      toast.success(t("wizard.committed"));
      onCommitted?.(r.data);
    } catch (e) {
      const errs = e.response?.data?.detail?.errors || [e.response?.data?.detail || "Commit failed"];
      toast.error(Array.isArray(errs) ? errs.join("; ") : String(errs));
    } finally { setCommitting(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="enrollment-wizard">
        <DialogHeader>
          <DialogTitle className="text-[#0A1F44]">{t("wizard.title")}</DialogTitle>
        </DialogHeader>

        {resumePrompt && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex items-center justify-between" data-testid="wizard-resume-prompt">
            <span>{t("wizard.resumePrompt", { date: new Date(resumePrompt.updated_at).toLocaleString() })}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { http.delete(`/enrollment/draft/${resumePrompt.id}`); setResumePrompt(null); setState(emptyState()); }}>{t("wizard.discard")}</Button>
              <Button size="sm" className="bg-[#0A1F44] text-white" onClick={() => { setState(resumePrompt.state); setStep(resumePrompt.step || 0); setDraftId(resumePrompt.id); setResumePrompt(null); }} data-testid="wizard-resume-ok">{t("wizard.resume")}</Button>
            </div>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-3" data-testid="wizard-stepper">
          {STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => i <= step && setStep(i)}
              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full transition ${i === step ? "bg-[#0A1F44] text-white" : i < step ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}
              data-testid={`wizard-step-${s}`}
            >
              {i < step && <Check size={11} />}
              {t(`wizard.step.${s}`)}
            </button>
          ))}
        </div>

        {/* Step bodies */}
        {step === 0 && (
          <FamilyStep state={state} setState={setState} families={families} />
        )}
        {step === 1 && (
          <StudentStep state={state} setState={setState} />
        )}
        {step === 2 && (
          <MedicalStep state={state} setState={setState} />
        )}
        {step === 3 && (
          <GdprStep state={state} setState={setState} />
        )}
        {step === 4 && (
          <PaymentStep state={state} setState={setState} ibanCheck={ibanCheck} validateIban={validateIban} />
        )}
        {step === 5 && (
          <ReviewStep state={state} />
        )}

        <DialogFooter className="flex flex-row items-center justify-between gap-2 mt-4">
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveDraft} data-testid="wizard-save-draft">{t("wizard.saveDraft")}</Button>
            <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back"><ChevronLeft size={14} className="mr-1" />{t("wizard.back")}</Button>
            {step < STEPS.length - 1 ? (
              <Button className="bg-[#0A1F44] text-white" onClick={() => setStep(step + 1)} data-testid="wizard-next">{t("wizard.next")}<ChevronRight size={14} className="ml-1" /></Button>
            ) : (
              <Button className="bg-[#C8102E] text-white" disabled={committing} onClick={commit} data-testid="wizard-commit">{committing ? t("common.loading") : t("wizard.confirm")}</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FamilyStep({ state, setState, families }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4" data-testid="wizard-family-step">
      <div className="flex gap-2">
        <Button variant={state.family_mode === "new" ? "default" : "outline"} onClick={() => setState({ ...state, family_mode: "new" })} className={state.family_mode === "new" ? "bg-[#0A1F44] text-white" : ""} data-testid="wizard-family-new">New family</Button>
        <Button variant={state.family_mode === "existing" ? "default" : "outline"} onClick={() => setState({ ...state, family_mode: "existing" })} className={state.family_mode === "existing" ? "bg-[#0A1F44] text-white" : ""} data-testid="wizard-family-existing">Use existing</Button>
      </div>
      {state.family_mode === "existing" ? (
        <Select value={state.family_id} onValueChange={(v) => setState({ ...state, family_id: v })}>
          <SelectTrigger data-testid="wizard-family-select"><SelectValue placeholder="Select a family…" /></SelectTrigger>
          <SelectContent>{families.map((f) => <SelectItem key={f.id} value={f.id}>{f.family_name}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Family name" required><Input value={state.family.family_name} onChange={(e) => setState({ ...state, family: { ...state.family, family_name: e.target.value } })} data-testid="wizard-family-name" /></Field>
          <Field label="Primary parent first name" required><Input value={state.family.primary_parent.first_name} onChange={(e) => setState({ ...state, family: { ...state.family, primary_parent: { ...state.family.primary_parent, first_name: e.target.value } } })} /></Field>
          <Field label="Primary parent last name" required><Input value={state.family.primary_parent.last_name} onChange={(e) => setState({ ...state, family: { ...state.family, primary_parent: { ...state.family.primary_parent, last_name: e.target.value } } })} /></Field>
          <Field label="NIF/NIE"><Input value={state.family.primary_parent.nif_nie} onChange={(e) => setState({ ...state, family: { ...state.family, primary_parent: { ...state.family.primary_parent, nif_nie: e.target.value.toUpperCase() } } })} /></Field>
          <Field label="Phone"><Input value={state.family.primary_parent.phone} onChange={(e) => setState({ ...state, family: { ...state.family, primary_parent: { ...state.family.primary_parent, phone: e.target.value } } })} /></Field>
          <Field label="Email"><Input value={state.family.primary_parent.email} onChange={(e) => setState({ ...state, family: { ...state.family, primary_parent: { ...state.family.primary_parent, email: e.target.value } } })} /></Field>
        </div>
      )}
    </div>
  );
}

function StudentStep({ state, setState }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="wizard-student-step">
      <Field label="First name" required><Input value={state.student.first_name} onChange={(e) => setState({ ...state, student: { ...state.student, first_name: e.target.value } })} data-testid="wizard-student-first" /></Field>
      <Field label="Last name" required><Input value={state.student.last_name} onChange={(e) => setState({ ...state, student: { ...state.student, last_name: e.target.value } })} data-testid="wizard-student-last" /></Field>
      <Field label="Date of birth" required><Input type="date" value={state.student.date_of_birth} onChange={(e) => setState({ ...state, student: { ...state.student, date_of_birth: e.target.value } })} data-testid="wizard-student-dob" /></Field>
      <Field label="Level">
        <Select value={state.student.level} onValueChange={(v) => setState({ ...state, student: { ...state.student, level: v } })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{CEFR.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Phone"><Input value={state.student.phone} onChange={(e) => setState({ ...state, student: { ...state.student, phone: e.target.value } })} /></Field>
      <Field label="DNI/NIE"><Input value={state.student.dni_nie} onChange={(e) => setState({ ...state, student: { ...state.student, dni_nie: e.target.value.toUpperCase() } })} /></Field>
    </div>
  );
}

function MedicalStep({ state, setState }) {
  // Keep a raw string in the input so commas/spaces/etc. type freely. Parse
  // into the array only on blur or on wizard step submit.
  const initialRaw = React.useMemo(() => (state.student.allergies || []).join(", "), []);
  const [raw, setRaw] = React.useState(initialRaw);
  const commit = (v) => {
    const arr = v.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 20);
    setState({ ...state, student: { ...state.student, allergies: arr } });
  };
  return (
    <div className="space-y-3 text-sm" data-testid="wizard-medical-step">
      <Field label="Allergies (comma-separated)">
        <Input
          value={raw}
          onChange={(e) => { setRaw(e.target.value); commit(e.target.value); }}
          onBlur={(e) => commit(e.target.value)}
          placeholder="e.g. peanut, dairy, pollen"
          data-testid="wizard-allergies-input"
        />
      </Field>
      <Field label="Emergency contact name">
        <Input value={state.student.emergency_contact.name} onChange={(e) => setState({ ...state, student: { ...state.student, emergency_contact: { ...state.student.emergency_contact, name: e.target.value } } })} />
      </Field>
      <Field label="Emergency contact phone">
        <Input value={state.student.emergency_contact.phone} onChange={(e) => setState({ ...state, student: { ...state.student, emergency_contact: { ...state.student.emergency_contact, phone: e.target.value } } })} />
      </Field>
      <p className="text-xs text-slate-500">Specific medical alerts can be added on the student detail page after enrollment.</p>
    </div>
  );
}

function GdprStep({ state, setState }) {
  const u = (k, v) => setState({ ...state, family: { ...state.family, gdpr: { ...state.family.gdpr, [k]: v } } });
  return (
    <div className="space-y-3 text-sm" data-testid="wizard-gdpr-step">
      <label className="flex items-center gap-2"><Checkbox checked={state.family.gdpr.data_processing_consent} onCheckedChange={(v) => u("data_processing_consent", !!v)} data-testid="wizard-gdpr-processing" /> Consent to data processing <span className="text-[#C8102E]">(required)</span></label>
      <label className="flex items-center gap-2"><Checkbox checked={state.family.gdpr.image_rights_consent} onCheckedChange={(v) => u("image_rights_consent", !!v)} /> Image rights</label>
      <label className="flex items-center gap-2"><Checkbox checked={state.family.gdpr.marketing_consent} onCheckedChange={(v) => u("marketing_consent", !!v)} /> Marketing communications</label>
    </div>
  );
}

function PaymentStep({ state, setState, ibanCheck, validateIban }) {
  const u = (k, v) => setState({ ...state, family: { ...state.family, billing: { ...state.family.billing, [k]: v } } });
  return (
    <div className="space-y-3 text-sm" data-testid="wizard-payment-step">
      <Field label="Payment method">
        <Select value={state.family.billing.payment_method} onValueChange={(v) => u("payment_method", v)}>
          <SelectTrigger data-testid="wizard-payment-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sepa">SEPA</SelectItem><SelectItem value="card">Card</SelectItem>
            <SelectItem value="transfer">Bank transfer</SelectItem><SelectItem value="cash">Cash</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label="Billing email"><Input value={state.family.billing.billing_email} onChange={(e) => u("billing_email", e.target.value)} /></Field>
      {state.family.billing.payment_method === "sepa" && (
        <>
          <Field label="IBAN">
            <div className="flex gap-2 items-center">
              <Input value={state.family.billing.iban} onChange={(e) => u("iban", e.target.value.toUpperCase())} onBlur={(e) => e.target.value && validateIban(e.target.value)} data-testid="wizard-iban" />
              {ibanCheck === "checking" && <Badge variant="outline">checking…</Badge>}
              {ibanCheck && ibanCheck.ok && <Badge className="bg-emerald-100 text-emerald-800" data-testid="wizard-iban-ok">valid</Badge>}
              {ibanCheck && ibanCheck.ok === false && <Badge className="bg-[#C8102E] text-white" data-testid="wizard-iban-bad">invalid</Badge>}
            </div>
          </Field>
          <Field label="BIC"><Input value={state.family.billing.bic} onChange={(e) => u("bic", e.target.value.toUpperCase())} data-testid="wizard-bic" /></Field>
        </>
      )}
    </div>
  );
}

function ReviewStep({ state }) {
  return (
    <div className="space-y-4 text-sm" data-testid="wizard-review-step">
      <section>
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Family</h4>
        <div className="bg-slate-50 rounded-lg p-3">
          {state.family_mode === "existing" ? "Existing family selected" : `${state.family.family_name} — ${state.family.primary_parent.first_name} ${state.family.primary_parent.last_name}`}
        </div>
      </section>
      <section>
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Student</h4>
        <div className="bg-slate-50 rounded-lg p-3">
          {state.student.first_name} {state.student.last_name} · DOB {state.student.date_of_birth} · Level {(state.student.level || "").toUpperCase()}
        </div>
      </section>
      <section>
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Payment</h4>
        <div className="bg-slate-50 rounded-lg p-3">{state.family.billing.payment_method}{state.family.billing.iban ? ` · ${state.family.billing.iban}` : ""}</div>
      </section>
      <section>
        <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">GDPR</h4>
        <div className="bg-slate-50 rounded-lg p-3">
          {state.family.gdpr.data_processing_consent ? "✓ Data processing" : "✗ Data processing"} · {state.family.gdpr.image_rights_consent ? "✓ Image" : "—"} · {state.family.gdpr.marketing_consent ? "✓ Marketing" : "—"}
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}{required ? <span className="text-[#C8102E]"> *</span> : null}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
